import React, { useRef, useState, useEffect } from 'react';
import { X, Printer } from 'lucide-react';

const ReceiptModal = ({ payment, onClose, apiCall }) => {
  const printRef = useRef(null);
  const [familyData, setFamilyData] = useState(null);
  const [allocations, setAllocations] = useState([]); // ✅ NEW: Payment allocations
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (payment) {
      if (payment.FamilyID) {
        loadReceiptData();
      } else {
        // No family for Bus Rentals, just set loading to false
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  const loadReceiptData = async () => {
    try {
      setLoading(true);
      
      // Load family data
      let family = null;
      try {
        family = await apiCall(`/families/${payment.FamilyID}`, { method: 'GET' });
        setFamilyData(family);
      } catch (error) {
        console.error('Error loading family data:', error);
      }
      
      // ✅ NEW: Load payment allocations to show what the payment was applied to
      try {
        const allocationData = await apiCall(`/payments/${payment.PaymentID}/allocations`, { method: 'GET' });
        console.log('Payment allocations:', allocationData);
        setAllocations(allocationData || []);
      } catch (error) {
        console.error('Error loading payment allocations:', error);
        setAllocations([]);
      }
      
    } catch (error) {
      console.error('Error loading receipt data:', error);
      setFamilyData(null);
    } finally {
      setLoading(false);
    }
  };

  if (!payment) {
    return null;
  }

  // Module name mapping function
  const getModuleDisplayName = (moduleName) => {
    const moduleNames = {
      'Afterschool': 'After School Program',
      'Bus': 'Transportation Service',
      'Activities': 'Activities Program',
      'BusRental': 'Bus Rental',
      'Tuition': 'Tuition Fees',
      'Registration': 'Registration Fees',
      'Other': 'Other Fees',
      'Daycare': 'Daycare Program',
      'ArtSchool': 'Art School Program',
    };
    return moduleNames[moduleName] || moduleName;
  };

  // ✅ NEW: Parse allocation description for nice display
  const parseAllocationDescription = (description) => {
    // Expected format: "John Smith (EB2) - Football - Football 2024-2025 - Trimester 1"
    // Or for Afterschool: "John Smith (EB2) - Full Day - October 2025"
    if (!description) return { raw: 'Payment allocation' };
    
    const parts = description.split(' - ');
    if (parts.length >= 2) {
      return {
        studentInfo: parts[0], // "John Smith (EB2)"
        category: parts[1],    // "Football" or "Full Day"
        activity: parts[2] || '', // "Football 2024-2025"
        period: parts[3] || '',   // "Trimester 1"
        raw: description
      };
    }
    return { raw: description };
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const windowPrint = window.open('', '', 'width=800,height=600');
    
    windowPrint.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Receipt #${payment.PaymentID}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              width: 210mm;
              height: auto;
              margin: 0;
              padding: 0;
              overflow: hidden;
            }
            body {
              font-family: Arial, sans-serif;
            }
            .receipt-wrapper {
              width: 210mm;
              max-height: 277mm;
              padding: 10mm;
              background: white;
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            .receipt-container {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 20px;
              width: 100%;
              height: auto;
              max-height: 257mm;
            }
            .column {
              display: flex;
              flex-direction: column;
            }
            .header-section {
              text-align: center;
              margin-bottom: 12px;
              padding-bottom: 10px;
              border-bottom: 2px solid #1f2937;
            }
            .school-name {
              font-size: 18px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 4px;
            }
            .location {
              font-size: 10px;
              color: #6b7280;
              margin-bottom: 2px;
            }
            .academic-year {
              font-size: 9px;
              color: #6b7280;
              font-weight: 600;
            }
            .module-section {
              text-align: center;
              margin-bottom: 14px;
            }
            .module-name {
              font-size: 16px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 4px;
            }
            .module-subtitle {
             font-size: 11px;
             color: #6b7280;
             margin-bottom: 2px;
             }
            .info-item {
              margin-bottom: 12px;
            }
            .info-label {
              font-size: 11px;
              color: #6b7280;
              margin-bottom: 2px;
            }
            .info-value {
              font-size: 14px;
              font-weight: bold;
              color: #1f2937;
            }
            .signature-section {
              margin-top: auto;
              padding-top: 16px;
              border-top: 1px solid #d1d5db;
            }
            .cashier-text {
              font-size: 11px;
              color: #6b7280;
              margin-bottom: 8px;
            }
            .signature-line {
              border-top: 2px solid #1f2937;
              margin-bottom: 4px;
            }
            .signature-label {
              font-size: 11px;
              color: #6b7280;
              text-align: center;
            }
            .family-box {
              background-color: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 12px;
              margin-bottom: 16px;
            }
            .family-title {
              font-size: 12px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 8px;
            }
            .family-info {
              font-size: 11px;
              color: #1f2937;
              margin: 4px 0;
            }
            .notes-box {
              background-color: #fef3c7;
              border: 1px solid #fbbf24;
              border-radius: 8px;
              padding: 12px;
              margin-bottom: 16px;
            }
            .notes-title {
              font-size: 12px;
              font-weight: bold;
              color: #92400e;
              margin-bottom: 8px;
            }
            .notes-text {
              font-size: 11px;
              color: #78350f;
              margin: 0;
              white-space: pre-wrap;
            }
            .allocations-section {
              margin-top: 12px;
              padding-top: 12px;
              border-top: 1px solid #e5e7eb;
            }
            .allocations-title {
              font-size: 11px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 8px;
            }
            .allocations-list {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            .allocation-item {
              font-size: 9px;
              padding: 4px 0;
              border-bottom: 1px dotted #e5e7eb;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
            }
            .allocation-desc {
              flex: 1;
              color: #374151;
              line-height: 1.3;
            }
            .allocation-amount {
              font-weight: 600;
              color: #1f2937;
              margin-left: 8px;
              white-space: nowrap;
            }
            .children-section {
              margin-top: 8px;
            }
            .children-title {
              font-size: 12px;
              font-weight: bold;
              color: #1f2937;
              margin-bottom: 8px;
            }
            .children-list {
              list-style: none;
              padding: 0;
            }
            .child-item {
              font-size: 10px;
              border-bottom: 1px solid #e5e7eb;
              padding: 4px 0;
              color: #1f2937;
            }
            .child-name {
              font-weight: 600;
            }
            .child-class {
              color: #6b7280;
              margin-left: 4px;
            }
            .amount-box {
              background-color: #eff6ff;
              border: 2px solid #93c5fd;
              border-radius: 8px;
              padding: 12px;
              text-align: center;
              margin-bottom: 16px;
            }
            .amount-label {
              font-size: 10px;
              font-weight: 600;
              color: #374151;
              margin-bottom: 6px;
            }
            .amount-value {
              font-size: 32px;
              font-weight: bold;
              color: #2563eb;
              margin-bottom: 6px;
            }
            .payment-method {
              font-size: 11px;
              color: #6b7280;
            }
            .stamp-section {
              margin-top: auto;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .thank-you {
              font-size: 10px;
              color: #6b7280;
              font-style: italic;
              margin-bottom: 12px;
              text-align: center;
            }
            .stamp-box {
              width: 90px;
              height: 90px;
              border: 2px dashed #9ca3af;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .stamp-text {
              font-size: 11px;
              color: #9ca3af;
              font-weight: 600;
              text-align: center;
              line-height: 1.3;
            }
            .cut-line {
              position: absolute;
              top: 148mm;
              left: 0;
              right: 0;
              border-top: 1px dashed #ccc;
              text-align: center;
              color: #999;
              font-size: 9px;
              padding-top: 2px;
            }
            @media print {
              .cut-line {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-wrapper">
            ${printContent.innerHTML}
          </div>
          <div class="cut-line">✂ Cut here (A5 size: 210mm x 148mm) ✂</div>
        </body>
      </html>
    `);
    
    windowPrint.document.close();
    windowPrint.focus();
    
    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800">Payment Receipt</h3>
          <div className="flex gap-2">
            <button 
              onClick={handlePrint} 
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
            >
              <Printer size={18} />
              Print
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-8" ref={printRef}>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading receipt data...</p>
            </div>
          ) : (
            <div className="receipt-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
              {/* Left Column */}
              <div className="column" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="header-section" style={{ textAlign: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #1f2937' }}>
                  <h1 className="school-name" style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                    Collège des Apôtres
                  </h1>
                  <p className="location" style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>
                    Jounieh, Lebanon
                  </p>
                </div>

                <div className="module-section" style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <h2 className="module-name" style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                    {getModuleDisplayName(payment.Module) || 'Payment'}
                  </h2>
                  <p className="module-subtitle" style={{ fontSize: '12px', color: '#6b7280' }}>
                    Payment Receipt
                  </p>
                  <p className="academic-year" style={{ fontSize: '10px', color: '#6b7280', fontWeight: '600' }}>
                    Academic Year: {payment.AcademicYear || 'N/A'}
                  </p>
                </div>

                <div className="info-item" style={{ marginBottom: '12px' }}>
                  <p className="info-label" style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>
                    Receipt Number:
                  </p>
                  <p className="info-value" style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                    #{payment.PaymentID}
                  </p>
                </div>

                <div className="info-item" style={{ marginBottom: '12px' }}>
                  <p className="info-label" style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>
                    Date:
                  </p>
                  <p className="info-value" style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937' }}>
                    {new Date(payment.PaymentDate).toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div className="signature-section" style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid #d1d5db' }}>
                  <p className="cashier-text" style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
                    Cashier: {payment.CreatedBy || 'N/A'}
                  </p>
                  <div className="signature-line" style={{ borderTop: '2px solid #1f2937', marginBottom: '4px' }}></div>
                  <p className="signature-label" style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center' }}>
                    Signature
                  </p>
                </div>
              </div>

              {/* Middle Column */}
              <div className="column" style={{ display: 'flex', flexDirection: 'column' }}>
                {payment.Module === 'BusRental' ? (
                  // Bus Rental Information
                  <div className="family-box" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                    <h3 className="family-title" style={{ fontSize: '12px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                      Rental Information
                    </h3>
                    <p className="family-info" style={{ fontSize: '11px', color: '#1f2937', margin: '4px 0' }}>
                      <strong>Trip ID:</strong> {payment.ReferenceNumber?.replace('RENTAL-', '#')}
                    </p>
                    <p className="family-info" style={{ fontSize: '11px', color: '#1f2937', margin: '4px 0' }}>
                      <strong>Details:</strong> {payment.Notes || 'Bus Rental Payment'}
                    </p>
                  </div>
                ) : (
                  // Family Information (for other modules)
                  <>
                <div className="family-box" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <h3 className="family-title" style={{ fontSize: '12px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                    Family Information
                  </h3>
                  <p className="family-info" style={{ fontSize: '11px', color: '#1f2937', margin: '4px 0' }}>
                    <strong>ID:</strong> {payment.FamilyID}
                  </p>
                  {familyData && (
                    <p className="family-info" style={{ fontSize: '11px', color: '#1f2937', margin: '4px 0' }}>
                      <strong>Father:</strong> {familyData.FatherName || 'N/A'}
                    </p>
                  )}
                </div>
                
                {/* Payment Notes - Bus Module Only */}
                {payment.Module === 'Bus' && payment.Notes && (
                  <div className="notes-box" style={{ backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                    <h3 className="notes-title" style={{ fontSize: '12px', fontWeight: 'bold', color: '#92400e', marginBottom: '8px' }}>
                      💡 Payment Notes
                    </h3>
                    <p className="notes-text" style={{ fontSize: '11px', color: '#78350f', margin: '0', whiteSpace: 'pre-wrap' }}>
                      {payment.Notes}
                    </p>
                  </div>
                )}
                </>
                )}

                {/* ✅ NEW: Payment Allocations Section - Shows what the payment was applied to */}
                {allocations.length > 0 ? (
                  <div className="allocations-section" style={{ flex: 1 }}>
                    <h3 className="allocations-title" style={{ fontSize: '12px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                      Payment Applied To:
                    </h3>
                    <ul className="allocations-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {allocations.map((alloc, idx) => {
                        const parsed = parseAllocationDescription(alloc.Description);
                        return (
                          <li key={idx} className="allocation-item" style={{ 
                            fontSize: '10px', 
                            padding: '6px 0', 
                            borderBottom: '1px dotted #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                          }}>
                            <div style={{ flex: 1, lineHeight: '1.4' }}>
                              {parsed.studentInfo ? (
                                <>
                                  <div style={{ fontWeight: '600', color: '#1f2937' }}>{parsed.studentInfo}</div>
                                  <div style={{ color: '#6b7280', fontSize: '9px' }}>
                                    {parsed.category}
                                    {parsed.activity && ` - ${parsed.activity}`}
                                    {parsed.period && ` - ${parsed.period}`}
                                  </div>
                                </>
                              ) : (
                                <div style={{ color: '#374151' }}>{parsed.raw}</div>
                              )}
                            </div>
                            <div style={{ fontWeight: '600', color: '#1f2937', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                              ${(alloc.Amount || 0).toFixed(2)}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  /* Fallback: Show children list if no allocations */
                  <div className="children-section">
                    <h3 className="children-title" style={{ fontSize: '12px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
                      Children:
                    </h3>
                    <ul className="children-list" style={{ listStyle: 'none', padding: 0 }}>
                      {(familyData && familyData.children && familyData.children.length > 0) ? (
                        familyData.children.map((child, idx) => (
                          <li key={idx} className="child-item" style={{ fontSize: '10px', borderBottom: '1px solid #e5e7eb', padding: '4px 0', color: '#1f2937' }}>
                            <span className="child-name" style={{ fontWeight: '600' }}>
                              {child.FirstName} {child.LastName}
                            </span>
                            <span className="child-class" style={{ color: '#6b7280', marginLeft: '4px' }}>
                              ({child.Class}{child.Section || ''})
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className="child-item" style={{ fontSize: '10px', color: '#6b7280' }}>
                          {loading ? 'Loading children data...' : 'No allocation details available'}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="column" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="amount-box" style={{ backgroundColor: '#eff6ff', border: '2px solid #93c5fd', borderRadius: '8px', padding: '16px', textAlign: 'center', marginBottom: '20px' }}>
                  <p className="amount-label" style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Amount Paid:
                  </p>
                  <p className="amount-value" style={{ fontSize: '36px', fontWeight: 'bold', color: '#2563eb', marginBottom: '8px' }}>
                    ${payment.Amount ? payment.Amount.toFixed(2) : '0.00'}
                  </p>
                  <p className="payment-method" style={{ fontSize: '11px', color: '#6b7280' }}>
                    Method: {payment.PaymentMethod || 'N/A'}
                  </p>
                </div>

                <div className="stamp-section" style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <p className="thank-you" style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', marginBottom: '12px', textAlign: 'center' }}>
                    Thank you for your payment
                  </p>
                  <div className="stamp-box" style={{ width: '90px', height: '90px', border: '2px dashed #9ca3af', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="stamp-text" style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textAlign: 'center', lineHeight: '1.3' }}>
                      OFFICIAL<br/>STAMP
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;